import { autorun } from 'mobx';
import { useEffect, useRef, useState } from 'react';

/**
 * observable 대상은 effect 내부에서 자동으로 인식되지 않으므로 autorun 으로 실행해줘야 한다
 */
export const useAutorun = (func: () => void, deps: unknown[] = []) => {
    useEffect(() => {
        autorun(func);
    }, deps);
};

/**
 * 뷰에서 mobx 인스턴스를 한 번만 초기화할 때 사용
 * - 생성자 인자가 변경되었을 때만 생성자를 새로 생성
 * @param Constructor 생성자
 * @param args 생성자 인자
 * @returns mobx 인스턴스
 */
export const useMobxInstance = <T, P extends unknown[]>(
    Constructor: new (...args: P) => T,
    ...args: P
): T => {
    const [instance, setInstance] = useState<T>(() => new Constructor(...args));
    const prevArgsRef = useRef<P>(args);

    useEffect(() => {
        // deps 배열이 변경되었는지 확인
        const argsChanged =
            !prevArgsRef.current ||
            prevArgsRef.current.length !== args.length ||
            prevArgsRef.current.some((arg, i) => arg !== args[i]);

        if (argsChanged) {
            setInstance(new Constructor(...args));
            prevArgsRef.current = args;
        }
    }, [Constructor, ...args]); // Constructor와 args를 의존성 배열에 추가

    return instance;
};
